import { Component, ChangeDetectionStrategy, input, output } from '@angular/core';
import { ToastAction, ToastStatus } from './toast.types';
import { ZagIcon } from '../icon';

/**
 * Individual toast notification component.
 *
 * @example
 * ```html
 * <zag-toast
 *   [id]="toast.id"
 *   [status]="toast.status"
 *   [title]="toast.title"
 *   [message]="toast.message"
 *   [dismissible]="toast.dismissible"
 *   (dismissed)="onDismiss($event)"
 * />
 * ```
 */
@Component({
  selector: 'zag-toast',
  template: `
    <div class="zag-toast__icon" aria-hidden="true">
      <i [class]="iconClass()"></i>
    </div>
    <div class="zag-toast__content">
      @if (title()) {
        <div class="zag-toast__title">{{ title() }}</div>
      }
      <div class="zag-toast__message">{{ message() }}</div>
      @if (action()) {
        <button type="button" class="zag-toast__action" (click)="action()!.onClick()">
          {{ action()!.label }}
        </button>
      }
    </div>
    @if (dismissible()) {
      <button type="button" class="zag-toast__close" (click)="onClose()" aria-label="Close notification">
        <span zagIcon="close"></span>
      </button>
    }
  `,
  styleUrl: './toast.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.zag-toast]': 'true',
    '[class.zag-toast--success]': 'status() === "success"',
    '[class.zag-toast--warning]': 'status() === "warning"',
    '[class.zag-toast--error]': 'status() === "error"',
    '[class.zag-toast--info]': 'status() === "info"',
    role: 'alert',
    '[attr.aria-live]': '"polite"',
  },
  imports: [ZagIcon],
})
export class ToastComponent {
  /** Unique identifier for the toast */
  readonly id = input.required<string>();

  /** Visual status of the toast */
  readonly status = input.required<ToastStatus>();

  /** Optional title */
  readonly title = input<string>();

  /** Message content */
  readonly message = input.required<string>();

  /** Whether the toast can be dismissed */
  readonly dismissible = input<boolean>(true);

  /** Optional call-to-action link */
  readonly action = input<ToastAction | undefined>(undefined);

  /** Emits the toast id when dismissed */
  readonly dismissed = output<string>();

  /** Get the icon class based on status */
  iconClass(): string {
    const icons: Record<ToastStatus, string> = {
      success: 'fa fa-check-circle',
      warning: 'fa fa-exclamation-triangle',
      error: 'fa fa-times-circle',
      info: 'fa fa-info-circle',
    };
    return icons[this.status()];
  }

  onClose(): void {
    this.dismissed.emit(this.id());
  }
}
